import { useEffect, useState, useMemo, useCallback } from "react";
import api, { formatErr } from "../lib/api";
import { T } from "../lib/i18n";
import { toast } from "sonner";
import { Scissors, Check, Send, ArrowLeft, ArrowRight } from "lucide-react";

const THREAD_COLORS = ["White", "Black", "Gold", "Silver", "Maroon", "Teal"];
const THREAD_HEX = {
  White: "#FAFAFA", Black: "#111111", Gold: "#D4AF37",
  Silver: "#C0C0C0", Maroon: "#800000", Teal: "#008080",
};

/** Sequential embroidery flow — Phase 3B rebuild
 *  Steps: 1) Item type  2) Fabric color (backend inventory, remaining_qty>0)
 *         3) Thread color (6 fixed)  4) Text (max 3 chars, case-preserved)
 *         5) Review + submit
 *  Rules: no font selector · atomic reserve on submit · premade guests blocked ·
 *         one order per eligible guest.
 */
export default function EmbroideryFlow({ guest }) {
  const [experience, setExperience] = useState(null);
  const [inventory, setInventory] = useState([]);
  const [myReqs, setMyReqs] = useState([]);
  const [queueEnabled, setQueueEnabled] = useState(true);

  const [step, setStep] = useState(1);
  const [itemType, setItemType] = useState("");
  const [color, setColor] = useState("");
  const [thread, setThread] = useState("");
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [exp, inv, mine, qs] = await Promise.allSettled([
        api.get("/experiences"),
        api.get("/embroidery/inventory"),
        api.get("/experiences/requests"),
        api.get("/embroidery/queue-state"),
      ]);
      if (exp.status === "fulfilled") {
        const e = exp.value.data.find((x) => x.kind === "embroidery");
        setExperience(e || null);
      }
      if (inv.status === "fulfilled") setInventory(inv.value.data);
      if (mine.status === "fulfilled") setMyReqs(mine.value.data);
      if (qs.status === "fulfilled") setQueueEnabled(!!qs.value.data.enabled);
    } catch (e) {
      // Silent — page recovers on next refresh cycle
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Live queue-state broadcast — updates guest UI in real time when staff toggles.
  useEffect(() => {
    const iv = setInterval(() => {
      api.get("/embroidery/queue-state").then(({ data }) => setQueueEnabled(!!data.enabled)).catch(() => {});
    }, 8000);
    return () => clearInterval(iv);
  }, []);

  const isPremadeGuest = guest?.favor_status === "PREMADE_DELIVERED" || guest?.favor_premade === true;

  const myReq = useMemo(
    () => myReqs.find((r) => r.experience_id === experience?.id && !["cancelled", "CANCELLED"].includes(r.status)),
    [myReqs, experience]
  );

  const inventoryByType = useMemo(() => {
    const map = { Pashmina: [], Handkerchief: [] };
    inventory.forEach((row) => {
      if (row.remaining_qty > 0 && map[row.item_type]) map[row.item_type].push(row);
    });
    return map;
  }, [inventory]);

  const itemHasStock = (type) => (inventoryByType[type] || []).length > 0;

  if (!experience) {
    return (
      <div className="card-elev p-5 text-center text-motif-muted fadein" data-testid="embroidery-inactive">
        {T("Embroidery is not open right now.")}
      </div>
    );
  }

  // Premade delivered → show static "delivered" card, block form
  if (isPremadeGuest) {
    return (
      <div className="card-elev p-5 fadein" data-testid="embroidery-premade">
        <div className="flex items-center gap-2 mb-3">
          <Scissors className="w-4 h-4 text-primary-motif" strokeWidth={1.5} />
          <h3 className="font-display text-xl text-motif">{T("Live Embroidery")}</h3>
        </div>
        <div className="p-4 rounded-xl bg-accent-motif text-motif">
          <div className="font-display text-xl">{T("Favor Premade and Delivered")}</div>
          <div className="text-xs text-motif-muted mt-1">
            {T("Your monogrammed favor was prepared ahead of time and is waiting for you at your seat.")}
          </div>
        </div>
      </div>
    );
  }

  // Already ordered → status card
  if (myReq) {
    return <MyOrderCard req={myReq} />;
  }

  // Phase 3C-2: queue closed — guests without an existing order see the exact closed message.
  if (!queueEnabled) {
    return (
      <div className="card-elev p-6 fadein" data-testid="embroidery-closed">
        <div className="flex items-center gap-2 mb-3">
          <Scissors className="w-4 h-4 text-primary-motif" strokeWidth={1.5} />
          <h3 className="font-display text-xl text-motif">{T("Live Embroidery")}</h3>
        </div>
        <div className="p-6 rounded-xl bg-accent-motif text-motif text-center">
          <div className="font-display text-lg leading-snug" data-testid="embroidery-closed-message">
            {T("Custom embroidery team has ended their queue for the night. Please grab a pashmina or handkerchief on your way out")}
          </div>
        </div>
      </div>
    );
  }

  const canProceed = step === 1 ? itemType : step === 2 ? color : step === 3 ? thread : step === 4 ? text.length >= 1 && text.length <= 3 : true;

  const keepsakeNote = (
    <div className="mt-3 text-[11px] text-motif-muted leading-relaxed" data-testid="embroidery-keepsake-note">
      {T("If you don't receive one tonight, stop by the embroidery station before leaving and we'll give you a blank keepsake to take home.")}
    </div>
  );

  const submit = async () => {
    if (submitting) return;
    const cleanText = text; // preserve case per Phase 3B spec
    if (cleanText.length === 0 || cleanText.length > 3) {
      toast.error(T("Text must be 1–3 characters."));
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/experiences/request", {
        experience_id: experience.id,
        item: itemType,
        options: { color, thread_color: thread },
        text: cleanText,
      });
      toast.success(T("Your embroidery is queued — we'll notify you when it's ready."));
      setStep(1); setItemType(""); setColor(""); setThread(""); setText("");
      refresh();
    } catch (e) {
      toast.error(formatErr(e));
      // Refresh inventory in case of a stale zero-stock race
      refresh();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4 fadein">
      <div className="flex items-center gap-2">
        <Scissors className="w-4 h-4 text-primary-motif" strokeWidth={1.5} />
        <h3 className="font-display text-xl text-motif">{T("Live Embroidery")}</h3>
      </div>
      <p className="text-sm text-motif-muted">{experience.description}</p>
      {keepsakeNote}

      <StepIndicator step={step} />

      <div className="card-elev p-4 space-y-4">
        {step === 1 && (
          <StepItem
            itemType={itemType}
            setItemType={(t) => { setItemType(t); setColor(""); }}
            itemHasStock={itemHasStock}
          />
        )}
        {step === 2 && (
          <StepColor
            itemType={itemType}
            colors={inventoryByType[itemType] || []}
            color={color}
            setColor={setColor}
          />
        )}
        {step === 3 && (
          <StepThread thread={thread} setThread={setThread} />
        )}
        {step === 4 && (
          <StepText text={text} setText={setText} />
        )}
        {step === 5 && (
          <StepReview itemType={itemType} color={color} thread={thread} text={text} inventory={inventory} />
        )}

        <div className="flex gap-2 pt-3 border-t border-motif">
          {step > 1 && (
            <button
              onClick={() => setStep((s) => s - 1)}
              className="btn-ghost text-xs flex items-center gap-1.5"
              data-testid="embroidery-back"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> {T("Back")}
            </button>
          )}
          {step < 5 ? (
            <button
              onClick={() => setStep((s) => s + 1)}
              disabled={!canProceed}
              className="btn-primary flex-1 flex items-center justify-center gap-1.5"
              data-testid="embroidery-next"
            >
              {T("Next")} <ArrowRight className="w-3.5 h-3.5" />
            </button>
          ) : (
            <button
              onClick={submit}
              disabled={submitting || !text}
              className="btn-primary flex-1 flex items-center justify-center gap-1.5"
              data-testid="embroidery-submit"
            >
              <Send className="w-3.5 h-3.5" />
              {submitting ? T("Reserving…") : T("Confirm & submit")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function StepIndicator({ step }) {
  const steps = [T("Item"), T("Color"), T("Thread"), T("Text"), T("Review")];
  return (
    <div className="flex items-center gap-1.5 text-[10px] tracking-luxe max-w-full overflow-x-auto no-scrollbar" data-testid="embroidery-step-indicator">
      {steps.map((label, i) => {
        const idx = i + 1;
        const done = idx < step;
        const current = idx === step;
        return (
          <div key={label} className="flex items-center gap-2 flex-1">
            <div
              className="w-6 h-6 rounded-full flex items-center justify-center text-[10px]"
              style={{
                background: done || current ? "var(--motif-primary)" : "var(--motif-accent)",
                color: done || current ? "var(--motif-primary-fg)" : "var(--motif-text-muted)",
              }}
            >
              {done ? "✓" : idx}
            </div>
            <span className={`whitespace-nowrap ${current ? "text-motif" : "text-motif-muted"}`}>{label}</span>
            {idx < steps.length && <div className="flex-1 h-px bg-motif-border" style={{ background: "var(--motif-border)" }} />}
          </div>
        );
      })}
    </div>
  );
}

function StepItem({ itemType, setItemType, itemHasStock }) {
  const items = [
    { id: "Handkerchief", label: "Handkerchief" },
    { id: "Pashmina", label: "Pashmina" },
  ];
  return (
    <div className="space-y-3">
      <div className="text-[10px] tracking-luxe text-motif-muted">{T("step 1 · pick your item")}</div>
      <div className="grid grid-cols-2 gap-3">
        {items.map((it) => {
          const stock = itemHasStock(it.id);
          const selected = itemType === it.id;
          return (
            <button
              key={it.id}
              onClick={() => stock && setItemType(it.id)}
              disabled={!stock}
              className="p-4 rounded-xl border-2 transition text-left"
              style={{
                borderColor: selected ? "var(--motif-primary)" : "var(--motif-border)",
                background: selected ? "var(--motif-accent)" : "transparent",
                opacity: stock ? 1 : 0.5,
                cursor: stock ? "pointer" : "not-allowed",
              }}
              data-testid={`embroidery-item-${it.id.toLowerCase()}`}
            >
              <div className="font-display text-xl text-motif">{it.label}</div>
              <div className="text-[10px] text-motif-muted mt-1">
                {stock ? T("Available") : T("Currently out of stock")}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function StepColor({ itemType, colors, color, setColor }) {
  if (colors.length === 0) {
    return (
      <div className="text-center text-motif-muted text-sm py-6" data-testid="embroidery-no-colors">
        No {itemType} colors are available right now.
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <div className="text-[10px] tracking-luxe text-motif-muted">
        {T("step 2 · pick a color")}{" "}({itemType.toLowerCase()})
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
        {colors.map((c) => {
          const selected = color === c.color;
          return (
            <button
              key={c.color}
              onClick={() => setColor(c.color)}
              className="p-2 rounded-xl border-2 transition text-left"
              style={{
                borderColor: selected ? "var(--motif-primary)" : "var(--motif-border)",
                background: selected ? "var(--motif-accent)" : "transparent",
              }}
              data-testid={`embroidery-color-${c.color.replace(/\s+/g, "-").toLowerCase()}`}
              aria-label={`${c.color} — ${c.remaining_qty} remaining`}
            >
              <div
                className="w-full h-10 rounded-lg mb-1.5 border"
                style={{ background: c.swatch_hex || "#CCCCCC", borderColor: "var(--motif-border)" }}
              />
              <div className="text-[11px] font-medium text-motif leading-tight">{c.color}</div>
              <div className="text-[9px] text-motif-muted">
                {c.remaining_qty <= 2 ? `${T("Low")} · ${c.remaining_qty} ${T("left")}` : T("Available")}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function StepThread({ thread, setThread }) {
  return (
    <div className="space-y-3">
      <div className="text-[10px] tracking-luxe text-motif-muted">{T("step 3 · thread color")}</div>
      <div className="grid grid-cols-3 gap-2">
        {THREAD_COLORS.map((t) => {
          const selected = thread === t;
          return (
            <button
              key={t}
              onClick={() => setThread(t)}
              className="p-2 rounded-xl border-2 transition text-left"
              style={{
                borderColor: selected ? "var(--motif-primary)" : "var(--motif-border)",
                background: selected ? "var(--motif-accent)" : "transparent",
              }}
              data-testid={`embroidery-thread-${t.toLowerCase()}`}
              aria-label={`Thread color ${t}`}
            >
              <div
                className="w-full h-8 rounded-lg mb-1.5 border"
                style={{ background: THREAD_HEX[t] || "#CCCCCC", borderColor: "var(--motif-border)" }}
              />
              <div className="text-[11px] font-medium text-motif">{t}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function StepText({ text, setText }) {
  // Enforce 3-char hard cap with Unicode-safe truncation
  const onChange = (e) => {
    const raw = e.target.value;
    // Split by grapheme (approximate: iterate code points and stop at 3)
    let out = "";
    let count = 0;
    for (const ch of raw) {
      if (count >= 3) break;
      out += ch;
      count += 1;
    }
    setText(out);
  };
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-[10px] tracking-luxe text-motif-muted">{T("step 4 · your text")}</div>
        <div className="text-[10px] font-medium text-motif" data-testid="embroidery-text-counter">
          {[...text].length} / 3
        </div>
      </div>
      <input
        className="input-motif tracking-widest text-center font-display text-2xl"
        placeholder="e.g. A&R"
        value={text}
        onChange={onChange}
        maxLength={3 * 4 /* allow multi-byte input; JS-side truncation enforces 3 code points */}
        data-testid="embroidery-text"
      />
      <div className="text-[11px] text-motif-muted">
        {T("Maximum 3 characters. Text is case-sensitive.")}
      </div>
    </div>
  );
}

function StepReview({ itemType, color, thread, text, inventory }) {
  const colorRow = inventory.find((r) => r.item_type === itemType && r.color === color);
  return (
    <div className="space-y-3" data-testid="embroidery-review">
      <div className="text-[10px] tracking-luxe text-motif-muted">{T("step 5 · review & confirm")}</div>
      <ReviewRow label="Item" value={itemType} />
      <div className="flex items-center justify-between">
        <span className="text-[11px] tracking-luxe text-motif-muted">{T("Fabric color")}</span>
        <div className="flex items-center gap-2">
          <div
            className="w-8 h-6 rounded-md border"
            style={{ background: colorRow?.swatch_hex || "#CCCCCC", borderColor: "var(--motif-border)" }}
          />
          <span className="font-medium text-motif">{color}</span>
        </div>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-[11px] tracking-luxe text-motif-muted">{T("Thread color")}</span>
        <div className="flex items-center gap-2">
          <div
            className="w-8 h-6 rounded-md border"
            style={{ background: THREAD_HEX[thread] || "#CCCCCC", borderColor: "var(--motif-border)" }}
          />
          <span className="font-medium text-motif">{thread}</span>
        </div>
      </div>
      <ReviewRow label="Embroidery text" value={<span className="font-display text-xl tracking-widest">{text}</span>} />
      <div className="p-3 rounded-xl bg-accent-motif text-[11px] text-motif-muted">
        Default font · one favor per guest · pickup at the Embroidery Pickup Table when ready.
      </div>
    </div>
  );
}

function ReviewRow({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[11px] tracking-luxe text-motif-muted">{label}</span>
      <span className="font-medium text-motif">{value}</span>
    </div>
  );
}

function MyOrderCard({ req }) {
  const raw = (req.status || "").toUpperCase();
  const status = raw === "READY" || raw === "PICKED_UP" ? "COMPLETED" : raw;
  const isComplete = status === "COMPLETED";
  return (
    <div className="card-elev p-5 space-y-3 fadein" data-testid="my-embroidery">
      <div className="text-[10px] tracking-luxe text-motif-muted">{T("your order")}</div>
      <div className="font-display text-2xl text-motif">
        {req.item} · <span className="tracking-widest">&ldquo;{req.text}&rdquo;</span>
      </div>
      <div className="text-xs text-motif-muted">
        {Object.entries(req.options || {}).map(([k, v]) => `${k.replace("_", " ")}: ${v}`).join(" · ")}
      </div>
      {isComplete ? (
        <div className="rounded-xl p-4 space-y-2" style={{ background: "#10b98118", border: "1px solid #10b981" }} data-testid="embroidery-complete">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: "#10b981" }}>
              <Check className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <div className="font-display text-2xl tracking-luxe" style={{ color: "#10b981" }}>{T("COMPLETE")}</div>
          </div>
          <div className="text-sm text-motif font-medium">
            {T("Your favor is ready for pickup at the Embroidery Pickup Table.")}
          </div>
        </div>
      ) : (
        <StatusBadge s={status.toLowerCase()} />
      )}
    </div>
  );
}

function StatusBadge({ s }) {
  // 3-state guest labels (Phase 3B simplified): PENDING · IN PROGRESS · (COMPLETE handled separately)
  const canonical = (s || "").toLowerCase();
  const mapped = canonical === "accepted" ? "in_progress" : canonical;
  const LABEL = {
    pending: "PENDING", in_progress: "IN PROGRESS",
    ready: "COMPLETE", picked_up: "COMPLETE", premade_delivered: "PRE-DELIVERED",
    cancelled: "CANCELLED",
  };
  const colors = {
    pending: "var(--motif-text-muted)",
    in_progress: "#f59e0b",
    ready: "#10b981",
    picked_up: "var(--motif-text-muted)",
    premade_delivered: "#10b981",
    cancelled: "#ef4444",
  };
  return (
    <span
      className="inline-block text-[10px] tracking-luxe px-2 py-1 rounded-full"
      style={{ background: "var(--motif-accent)", color: colors[mapped] || "var(--motif-text)" }}
    >
      {T(LABEL[mapped] || mapped.replace("_", " ").toUpperCase())}
    </span>
  );
}
